import { useCallback, useEffect, useRef, useState } from 'react'

export interface BatchRequestState<TRequest, TResponse> {
  loading: boolean
  error: unknown
  data: TResponse | undefined
  completed: boolean
  aborted: boolean
  request?: TRequest
}

export interface SettledResult<TResponse> {
  id: string
  success: boolean
  data?: TResponse
  error?: unknown
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface UseBatchMutationOptions<TRequest, TResponse> {
  onSuccess?: (id: string, data: TResponse) => void
  onFailure?: (id: string, error: unknown) => void
  onAbort?: (id: string) => void
  onSettled?: (id: string, data: TResponse | undefined, error: unknown | undefined) => void
}

interface UseBatchMutationReturn<TRequest, TResponse> {
  /** Trigger a mutation request. Returns undefined if the request was aborted or failed. */
  trigger: (id: string, request: TRequest) => Promise<TResponse | undefined>
  /** Trigger a mutation request and return a settled result (never throws). */
  triggerSettled: (id: string, request: TRequest) => Promise<SettledResult<TResponse>>
  /** Abort a specific request by ID, or all requests if no ID is provided. */
  abort: (id?: string) => void
  /** Reset state for a specific request by ID, or all requests if no ID is provided. */
  reset: (id?: string) => void
  /** Get the state of a specific request by ID. */
  getState: (id: string) => BatchRequestState<TRequest, TResponse> | undefined
  /** Get all request states as a record keyed by request ID. */
  getAllStates: () => Record<string, BatchRequestState<TRequest, TResponse>>
  /** Check if a specific request (or any request if no ID) is loading. */
  isLoading: (id?: string) => boolean
  /** Check if a specific request (or any request if no ID) has an error. */
  hasError: (id?: string) => boolean
  /** Check if all requests have completed (true only if at least one request exists). */
  isAllCompleted: () => boolean
  /** Check if any request is currently loading. */
  isAnyLoading: () => boolean
  /** Check if a specific request (or any request if no ID) was aborted. */
  isAborted: (id?: string) => boolean
  /** Get all errors from failed requests as a record keyed by request ID. */
  getErrors: () => Record<string, unknown>
  /** Get all successful results as a record keyed by request ID. */
  getSuccessfulResults: () => Record<string, TResponse>
}

/**
 * Hook for handling batch requests with individual state tracking for each request.
 * Each request is identified by a unique ID and has its own loading, error, and result state.
 *
 * @param mutationTrigger - The RTK Query mutation trigger function (from useXxxMutation hook)
 * @param options - Optional callbacks for success and failure
 * @returns Object with methods to trigger requests, get states, and check status
 *
 * @example
 * ```tsx
 * import { useCreatePrescriptionMutation } from '../services/prescriptions'
 * import { useBatchMutation } from '../hooks/useBatchMutation'
 *
 * function MyComponent() {
 *   const [createPrescription] = useCreatePrescriptionMutation()
 *   const {
 *     trigger,
 *     getState,
 *     isLoading,
 *     isAnyLoading,
 *     abort,
 *   } = useBatchMutation(createPrescription, {
 *     onSuccess: (id, data) => console.log(`${id} succeeded:`, data),
 *     onFailure: (id, error) => console.error(`${id} failed:`, error),
 *     onSettled: (id, data, error) => {
 *       console.log(`${id} settled`, { data, error })
 *     },
 *   })
 *
 *   const handleSubmit = async () => {
 *     // Pass the full request body as the second parameter
 *     // The request body should match the mutation's expected type
 *     const request1: CreatePrescriptionRequest = {
 *       newPrescription: {
 *         scid: 'scid-1',
 *         guid: 'guid-1',
 *         prescriberId: 'prescriber-id',
 *         patientId: 'patient-id',
 *         items: [...],
 *         // ... other fields
 *       },
 *       // config is optional - the hook will inject the abort signal automatically
 *       config: {
 *         headers: { 'custom-header': 'value' },
 *       },
 *     }
 *
 *     const request2: CreatePrescriptionRequest = {
 *       newPrescription: {
 *         scid: 'scid-2',
 *         guid: 'guid-2',
 *         // ... other fields
 *       },
 *     }
 *
 *     // Trigger multiple requests - pass unique ID and request body
 *     // Use trigger() if you want Promise.all to fail fast on first error
 *     await Promise.all([
 *       trigger('prescription-1', request1),
 *       trigger('prescription-2', request2),
 *     ])
 *
 *     // OR use triggerSettled() to get all responses (successful and failed)
 *     const results = await Promise.all([
 *       triggerSettled('prescription-1', request1),
 *       triggerSettled('prescription-2', request2),
 *     ])
 *     // results will contain all responses:
 *     // [{ id: 'prescription-1', success: true, data: ... }, { id: 'prescription-2', success: false, error: ... }]
 *
 *     // Check individual request state
 *     const state1 = getState('prescription-1')
 *     if (state1?.data) {
 *       console.log('Request 1 succeeded:', state1.data)
 *     }
 *
 *     // Abort a specific request
 *     abort('prescription-1')
 *
 *     // Abort all requests
 *     abort()
 *   }
 * }
 * ```
 *
 * @remarks
 * - The mutation trigger function is passed when creating the hook
 * - The request body passed to `trigger()` should match the mutation's expected type
 * - If the request has a `config` property, the hook will automatically inject the abort signal
 * - If the request doesn't have a `config` property, you can still use abort, but the signal won't be passed to the API call
 * - All pending requests are automatically aborted when the component unmounts
 * - Calling `trigger()` with the same ID while a request is pending returns the existing promise (deduplication)
 * - Callback execution order: onSuccess/onFailure -> onSettled
 * - All controller and pending request refs are properly cleaned up after completion or abort
 */
export function useBatchMutation<TRequest, TResponse>(
  mutationTrigger: (arg: any) => Promise<any>,
  options: UseBatchMutationOptions<TRequest, TResponse> = {}
): UseBatchMutationReturn<TRequest, TResponse> {
  const [states, setStates] = useState<Record<string, BatchRequestState<TRequest, TResponse>>>({})
  const pendingRequestsRef = useRef<Map<string, Promise<TResponse>>>(new Map())
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Use ref to avoid stale closures in callbacks
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  }, [options])

  // Cleanup on unmount - abort all pending requests
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => {
        if (!controller.signal.aborted) {
          controller.abort()
        }
      })
      abortControllersRef.current.clear()
      pendingRequestsRef.current.clear()
    }
  }, [])

  const trigger = useCallback(
    async (id: string, request: TRequest): Promise<TResponse | undefined> => {
      // If there's already a pending request for this ID, return it
      const existingRequest = pendingRequestsRef.current.get(id)
      if (existingRequest) {
        return existingRequest
      }

      // Create AbortController for this request
      const controller = new AbortController()
      abortControllersRef.current.set(id, controller)

      // Inject abort signal into request if it has a config property
      let requestWithSignal = request
      if (request && typeof request === 'object' && 'config' in request) {
        requestWithSignal = {
          ...request,
          config: {
            ...(request as any).config,
            signal: controller.signal,
          },
        } as TRequest
      }

      // Initialize state for this request
      setStates((prev) => ({
        ...prev,
        [id]: {
          loading: true,
          error: undefined,
          data: undefined,
          completed: false,
          aborted: false,
          request,
        },
      }))

      // Create the promise
      const requestPromise = (async () => {
        try {
          const result = await mutationTrigger(requestWithSignal as any)

          // Check if request was aborted
          if (controller.signal.aborted) {
            const abortError = new Error('Request aborted')
            setStates((prev) => ({
              ...prev,
              [id]: {
                loading: false,
                error: abortError,
                data: undefined,
                completed: true,
                aborted: true,
                request,
              },
            }))
            optionsRef.current.onAbort?.(id)
            optionsRef.current.onSettled?.(id, undefined, abortError)
            throw abortError
          }

          // RTK Query mutations return { data: TResponse } or { error: TError }
          // Handle both cases
          let data: TResponse | undefined
          let error: unknown | undefined

          if (
            result &&
            typeof result === 'object' &&
            'data' in result &&
            result.data !== undefined
          ) {
            data = result.data as TResponse
          } else if (result && typeof result === 'object' && 'error' in result) {
            error = result.error
          } else {
            // Fallback: assume the result itself is the data
            data = result as TResponse
          }

          if (error) {
            setStates((prev) => ({
              ...prev,
              [id]: {
                loading: false,
                error,
                data: undefined,
                completed: true,
                aborted: false,
                request,
              },
            }))

            optionsRef.current.onFailure?.(id, error)
            optionsRef.current.onSettled?.(id, undefined, error)
            throw error
          }

          if (data === undefined) {
            const undefinedError = new Error('Request returned undefined data')
            setStates((prev) => ({
              ...prev,
              [id]: {
                loading: false,
                error: undefinedError,
                data: undefined,
                completed: true,
                aborted: false,
                request,
              },
            }))
            optionsRef.current.onFailure?.(id, undefinedError)
            optionsRef.current.onSettled?.(id, undefined, undefinedError)
            throw undefinedError
          }

          setStates((prev) => ({
            ...prev,
            [id]: {
              loading: false,
              error: undefined,
              data,
              completed: true,
              aborted: false,
              request,
            },
          }))

          optionsRef.current.onSuccess?.(id, data)
          optionsRef.current.onSettled?.(id, data, undefined)
          return data
        } catch (error: unknown) {
          // Check if aborted
          const isAbortError =
            controller.signal.aborted ||
            (error instanceof Error &&
              (error.name === 'AbortError' || error.message === 'Request aborted'))

          if (isAbortError) {
            setStates((prev) => ({
              ...prev,
              [id]: {
                loading: false,
                error: error,
                data: undefined,
                completed: true,
                aborted: true,
                request,
              },
            }))
            optionsRef.current.onAbort?.(id)
            optionsRef.current.onSettled?.(id, undefined, error)
            throw error
          }

          const errorData = (error as any)?.data || error

          setStates((prev) => ({
            ...prev,
            [id]: {
              loading: false,
              error: errorData,
              data: undefined,
              completed: true,
              aborted: false,
              request,
            },
          }))

          optionsRef.current.onFailure?.(id, errorData)
          optionsRef.current.onSettled?.(id, undefined, errorData)
          throw errorData
        } finally {
          // Always clean up refs to prevent memory leaks
          pendingRequestsRef.current.delete(id)
          abortControllersRef.current.delete(id)
        }
      })()

      // Store the promise
      pendingRequestsRef.current.set(id, requestPromise)

      return await requestPromise
    },
    [mutationTrigger]
  )

  const triggerSettled = useCallback(
    async (id: string, request: TRequest): Promise<SettledResult<TResponse>> => {
      try {
        const data = await trigger(id, request)
        return {
          id,
          success: true,
          data,
        }
      } catch (error) {
        return {
          id,
          success: false,
          error,
        }
      }
    },
    [trigger]
  )

  const abort = useCallback((id?: string) => {
    if (id) {
      // Abort specific request
      const controller = abortControllersRef.current.get(id)
      if (controller && !controller.signal.aborted) {
        controller.abort()
        // Note: The promise's finally block will clean up refs
        setStates((prev) => {
          const state = prev[id]
          if (state && !state.completed) {
            const abortError = state.error || new Error('Request aborted')
            return {
              ...prev,
              [id]: {
                ...state,
                loading: false,
                completed: true,
                aborted: true,
                error: abortError,
              },
            }
          }
          return prev
        })
        optionsRef.current.onAbort?.(id)
      }
    } else {
      // Abort all requests - batch state updates to prevent multiple re-renders
      const controllersToAbort: string[] = []
      abortControllersRef.current.forEach((controller, requestId) => {
        if (!controller.signal.aborted) {
          controller.abort()
          controllersToAbort.push(requestId)
        }
      })

      if (controllersToAbort.length > 0) {
        setStates((prev) => {
          const updated = { ...prev }
          controllersToAbort.forEach((requestId) => {
            const state = updated[requestId]
            if (state && !state.completed) {
              const abortError = state.error || new Error('Request aborted')
              updated[requestId] = {
                ...state,
                loading: false,
                completed: true,
                aborted: true,
                error: abortError,
              }
            }
          })
          return updated
        })

        // Call callbacks after state update
        controllersToAbort.forEach((requestId) => {
          optionsRef.current.onAbort?.(requestId)
        })
      }
    }
  }, [])

  const reset = useCallback((id?: string) => {
    if (id) {
      // Reset specific request
      setStates((prev) => {
        const newStates = { ...prev }
        delete newStates[id]
        return newStates
      })
      pendingRequestsRef.current.delete(id)
      // Abort the controller if it exists
      const controller = abortControllersRef.current.get(id)
      if (controller && !controller.signal.aborted) {
        controller.abort()
      }
      abortControllersRef.current.delete(id)
    } else {
      // Reset all requests
      setStates({})
      pendingRequestsRef.current.clear()
      // Abort all controllers
      abortControllersRef.current.forEach((controller) => {
        if (!controller.signal.aborted) {
          controller.abort()
        }
      })
      abortControllersRef.current.clear()
    }
  }, [])

  const getState = useCallback(
    (id: string): BatchRequestState<TRequest, TResponse> | undefined => {
      return states[id]
    },
    [states]
  )

  const getAllStates = useCallback(() => {
    return states
  }, [states])

  const isLoading = useCallback(
    (id?: string): boolean => {
      if (id) {
        return states[id]?.loading ?? false
      }
      return Object.values(states).some((state) => state.loading)
    },
    [states]
  )

  const hasError = useCallback(
    (id?: string): boolean => {
      if (id) {
        return !!states[id]?.error
      }
      return Object.values(states).some((state) => !!state.error)
    },
    [states]
  )

  const isAllCompleted = useCallback((): boolean => {
    const allStates = Object.values(states)
    if (allStates.length === 0) return false
    return allStates.every((state) => state.completed)
  }, [states])

  const isAnyLoading = useCallback((): boolean => {
    return Object.values(states).some((state) => state.loading)
  }, [states])

  const isAborted = useCallback(
    (id?: string): boolean => {
      if (id) {
        return states[id]?.aborted ?? false
      }
      return Object.values(states).some((state) => state.aborted)
    },
    [states]
  )

  const getErrors = useCallback((): Record<string, unknown> => {
    const errors: Record<string, unknown> = {}
    Object.entries(states).forEach(([id, state]) => {
      if (state.error) {
        errors[id] = state.error
      }
    })
    return errors
  }, [states])

  const getSuccessfulResults = useCallback((): Record<string, TResponse> => {
    const results: Record<string, TResponse> = {}
    Object.entries(states).forEach(([id, state]) => {
      if (state.data !== undefined && !state.error) {
        results[id] = state.data
      }
    })
    return results
  }, [states])

  return {
    trigger,
    triggerSettled,
    abort,
    reset,
    getState,
    getAllStates,
    isLoading,
    hasError,
    isAllCompleted,
    isAnyLoading,
    isAborted,
    getErrors,
    getSuccessfulResults,
  }
}
